import React, { useRef } from 'react';
import { motion, useInView } from 'framer-motion';
import { Link } from 'react-router-dom';
import { useLanguage } from '../lib/LanguageContext';
import { translations } from '../lib/translations';
// Small abstract node decoration for member cards
function MemberNodeIcon() {
  return <svg width="32" height="32" viewBox="0 0 32 32" fill="none" xmlns="http://www.w3.org/2000/svg" style={{
    marginBottom: '1.5rem'
  }} data-id="element-81">
      <circle cx="8" cy="16" r="3" fill="var(--black)" data-id="element-82" />
      <circle cx="24" cy="8" r="2" fill="var(--grey-300)" data-id="element-83" />
      <circle cx="24" cy="24" r="2" fill="var(--red)" data-id="element-84" />
      <path d="M10 15L22 9" stroke="var(--grey-300)" strokeWidth="1" strokeDasharray="2 2" data-id="element-85" />
      <path d="M10 17L22 23" stroke="var(--grey-300)" strokeWidth="1" strokeDasharray="2 2" data-id="element-86" />
    </svg>;
}
// Global Team Structure Network Visualization
function GlobalNetwork() {
  const {
    lang
  } = useLanguage();
  const cnLabel = lang === 'zh' ? '中国' : 'CHINA';
  const jpLabel = lang === 'zh' ? '日本' : 'JAPAN';
  const usLabel = lang === 'zh' ? '美国' : 'US';
  return <div style={{
    width: '100%',
    height: '200px',
    position: 'relative',
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    margin: '4rem 0 2rem'
  }} data-id="element-87">
      <svg width="100%" height="100%" viewBox="0 0 700 200" fill="none" xmlns="http://www.w3.org/2000/svg" style={{
      maxWidth: '700px'
    }} data-id="element-88">
        {/* Connection lines: China–Japan */}
        <motion.path d="M 150 100 Q 275 55 350 100" stroke="var(--grey-300)" strokeWidth="1" strokeDasharray="4 4" fill="none" initial={{
        pathLength: 0,
        opacity: 0
      }} whileInView={{
        pathLength: 1,
        opacity: 1
      }} viewport={{
        once: true
      }} transition={{
        duration: 1.2,
        ease: 'easeInOut'
      }} data-id="element-89" />
        <motion.path d="M 150 100 Q 275 145 350 100" stroke="var(--red)" strokeWidth="1" strokeDasharray="4 4" fill="none" initial={{
        pathLength: 0,
        opacity: 0
      }} whileInView={{
        pathLength: 1,
        opacity: 0.5
      }} viewport={{
        once: true
      }} transition={{
        duration: 1.2,
        ease: 'easeInOut',
        delay: 0.15
      }} data-id="element-90" />

        {/* Connection lines: Japan–US */}
        <motion.path d="M 350 100 Q 475 55 550 100" stroke="var(--grey-300)" strokeWidth="1" strokeDasharray="4 4" fill="none" initial={{
        pathLength: 0,
        opacity: 0
      }} whileInView={{
        pathLength: 1,
        opacity: 1
      }} viewport={{
        once: true
      }} transition={{
        duration: 1.2,
        ease: 'easeInOut',
        delay: 0.3
      }} data-id="element-91" />
        <motion.path d="M 350 100 Q 475 145 550 100" stroke="var(--red)" strokeWidth="1" strokeDasharray="4 4" fill="none" initial={{
        pathLength: 0,
        opacity: 0
      }} whileInView={{
        pathLength: 1,
        opacity: 0.5
      }} viewport={{
        once: true
      }} transition={{
        duration: 1.2,
        ease: 'easeInOut',
        delay: 0.45
      }} data-id="element-92" />

        {/* Connection line: China–US (direct, subtle) */}
        <motion.path d="M 150 100 Q 350 30 550 100" stroke="var(--grey-300)" strokeWidth="1" strokeDasharray="4 4" fill="none" initial={{
        pathLength: 0,
        opacity: 0
      }} whileInView={{
        pathLength: 1,
        opacity: 0.3
      }} viewport={{
        once: true
      }} transition={{
        duration: 1.5,
        ease: 'easeInOut',
        delay: 0.6
      }} data-id="element-93" />

        {/* Intermediate nodes */}
        <motion.circle cx="250" cy="72" r="3" fill="var(--black)" initial={{
        scale: 0
      }} whileInView={{
        scale: 1
      }} viewport={{
        once: true
      }} transition={{
        delay: 1
      }} data-id="element-94" />
        <motion.circle cx="250" cy="128" r="2" fill="var(--red)" initial={{
        scale: 0
      }} whileInView={{
        scale: 1
      }} viewport={{
        once: true
      }} transition={{
        delay: 1.1
      }} data-id="element-95" />
        <motion.circle cx="450" cy="72" r="3" fill="var(--black)" initial={{
        scale: 0
      }} whileInView={{
        scale: 1
      }} viewport={{
        once: true
      }} transition={{
        delay: 1.2
      }} data-id="element-96" />
        <motion.circle cx="450" cy="128" r="2" fill="var(--red)" initial={{
        scale: 0
      }} whileInView={{
        scale: 1
      }} viewport={{
        once: true
      }} transition={{
        delay: 1.3
      }} data-id="element-97" />
        <motion.circle cx="350" cy="45" r="2" fill="var(--grey-500)" initial={{
        scale: 0
      }} whileInView={{
        scale: 1
      }} viewport={{
        once: true
      }} transition={{
        delay: 1.4
      }} data-id="element-98" />

        {/* China Node */}
        <motion.g initial={{
        opacity: 0,
        x: -20
      }} whileInView={{
        opacity: 1,
        x: 0
      }} viewport={{
        once: true
      }} transition={{
        duration: 0.8
      }} data-id="element-99">
          <circle cx="150" cy="100" r="40" fill="var(--white)" stroke="var(--black)" strokeWidth="1" data-id="element-100" />
          <circle cx="150" cy="100" r="34" fill="none" stroke="var(--grey-300)" strokeWidth="1" strokeDasharray="2 2" data-id="element-101" />
          <text x="150" y="104" textAnchor="middle" fontFamily='"IBM Plex Mono", monospace' fontSize="12" fill="var(--black)" letterSpacing="0.1em" data-id="element-102">
            {cnLabel}
          </text>
        </motion.g>

        {/* Japan Node */}
        <motion.g initial={{
        opacity: 0,
        y: 20
      }} whileInView={{
        opacity: 1,
        y: 0
      }} viewport={{
        once: true
      }} transition={{
        duration: 0.8,
        delay: 0.15
      }} data-id="element-103">
          <circle cx="350" cy="100" r="40" fill="var(--white)" stroke="var(--black)" strokeWidth="1" data-id="element-104" />
          <circle cx="350" cy="100" r="34" fill="none" stroke="var(--grey-300)" strokeWidth="1" strokeDasharray="2 2" data-id="element-105" />
          <text x="350" y="104" textAnchor="middle" fontFamily='"IBM Plex Mono", monospace' fontSize="12" fill="var(--black)" letterSpacing="0.1em" data-id="element-106">
            {jpLabel}
          </text>
        </motion.g>

        {/* US Node */}
        <motion.g initial={{
        opacity: 0,
        x: 20
      }} whileInView={{
        opacity: 1,
        x: 0
      }} viewport={{
        once: true
      }} transition={{
        duration: 0.8,
        delay: 0.3
      }} data-id="element-107">
          <circle cx="550" cy="100" r="40" fill="var(--white)" stroke="var(--black)" strokeWidth="1" data-id="element-108" />
          <circle cx="550" cy="100" r="34" fill="none" stroke="var(--grey-300)" strokeWidth="1" strokeDasharray="2 2" data-id="element-109" />
          <text x="550" y="104" textAnchor="middle" fontFamily='"IBM Plex Mono", monospace' fontSize="12" fill="var(--black)" letterSpacing="0.1em" data-id="element-110">
            {usLabel}
          </text>
        </motion.g>
      </svg>
    </div>;
}
// Abstract Founder Visual
function FounderVisual() {
  return <div style={{
    width: '100%',
    height: '100%',
    minHeight: '300px',
    position: 'relative',
    overflow: 'hidden',
    backgroundColor: '#0a0a0a',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center'
  }} data-id="element-111">
      <svg width="100%" height="100%" viewBox="0 0 400 400" preserveAspectRatio="xMidYMid slice" xmlns="http://www.w3.org/2000/svg" data-id="element-112">
        {/* Grid */}
        <pattern id="grid" width="40" height="40" patternUnits="userSpaceOnUse" data-id="element-113">
          <path d="M 40 0 L 0 0 0 40" fill="none" stroke="rgba(255,255,255,0.05)" strokeWidth="1" data-id="element-114" />
        </pattern>
        <rect width="100%" height="100%" fill="url(#grid)" data-id="element-115" />

        {/* Network Lines */}
        <g stroke="rgba(255,255,255,0.15)" strokeWidth="1" data-id="element-116">
          <line x1="100" y1="100" x2="200" y2="200" data-id="element-117" />
          <line x1="300" y1="100" x2="200" y2="200" data-id="element-118" />
          <line x1="200" y1="200" x2="200" y2="300" data-id="element-119" />
          <line x1="100" y1="300" x2="200" y2="200" data-id="element-120" />
          <line x1="300" y1="300" x2="200" y2="200" data-id="element-121" />

          <line x1="100" y1="100" x2="150" y2="50" stroke="var(--red)" strokeOpacity="0.5" data-id="element-122" />
          <line x1="300" y1="100" x2="250" y2="50" data-id="element-123" />
          <line x1="100" y1="300" x2="50" y2="250" data-id="element-124" />
          <line x1="300" y1="300" x2="350" y2="250" stroke="var(--red)" strokeOpacity="0.5" data-id="element-125" />
        </g>

        {/* Nodes */}
        <circle cx="100" cy="100" r="4" fill="rgba(255,255,255,0.8)" data-id="element-126" />
        <circle cx="300" cy="100" r="4" fill="rgba(255,255,255,0.8)" data-id="element-127" />
        <circle cx="200" cy="200" r="6" fill="var(--red)" data-id="element-128" />
        <circle cx="100" cy="300" r="4" fill="rgba(255,255,255,0.8)" data-id="element-129" />
        <circle cx="300" cy="300" r="4" fill="rgba(255,255,255,0.8)" data-id="element-130" />
        <circle cx="200" cy="300" r="4" fill="rgba(255,255,255,0.4)" data-id="element-131" />

        <circle cx="150" cy="50" r="3" fill="var(--red)" data-id="element-132" />
        <circle cx="250" cy="50" r="3" fill="rgba(255,255,255,0.4)" data-id="element-133" />
        <circle cx="50" cy="250" r="3" fill="rgba(255,255,255,0.4)" data-id="element-134" />
        <circle cx="350" cy="250" r="3" fill="var(--red)" data-id="element-135" />

        {/* Math Symbols */}
        <g fontFamily='"IBM Plex Math", "IBM Plex Sans", sans-serif' fontSize="14" fill="rgba(255,255,255,0.1)" data-id="element-136">
          <text x="60" y="80" data-id="element-137">
            ∂
          </text>
          <text x="320" y="140" data-id="element-138">
            ∇
          </text>
          <text x="120" y="280" data-id="element-139">
            Σ
          </text>
          <text x="280" y="260" data-id="element-140">
            ∫
          </text>
          <text x="220" y="180" fill="rgba(196,30,30,0.3)" data-id="element-141">
            f(x)
          </text>
        </g>
      </svg>
    </div>;
}
export function TeamSection() {
  const {
    lang
  } = useLanguage();
  const t = translations[lang].about;
  const team = t.teamData;
  const renderName = (name: string, nameZh: string) => {
    if (lang === 'zh') {
      return <>
          {nameZh}{' '}
          <span style={{
          fontSize: '0.85em',
          fontWeight: 400,
          color: 'var(--grey-500)',
          marginLeft: '0.5rem'
        }} data-id="element-142">
            {name}
          </span>
        </>;
    }
    return <>
        {name}{' '}
        <span style={{
        fontSize: '0.85em',
        fontWeight: 400,
        color: 'var(--grey-500)',
        marginLeft: '0.5rem'
      }} data-id="element-143">
          {nameZh}
        </span>
      </>;
  };
  return <section style={{
    maxWidth: '64rem',
    margin: '0 auto',
    padding: '8rem 2rem'
  }} data-id="element-144">
      <motion.div initial={{
      opacity: 0,
      y: 20
    }} whileInView={{
      opacity: 1,
      y: 0
    }} viewport={{
      once: true
    }} transition={{
      duration: 0.7
    }} data-id="element-145">
        <p style={{
        fontFamily: '"IBM Plex Mono", monospace',
        fontSize: '0.65rem',
        letterSpacing: '0.3em',
        textTransform: 'uppercase',
        color: 'var(--grey-500)',
        marginBottom: '2.5rem'
      }} data-id="element-146">
          {t.teamLabel}
        </p>
        <p style={{
        fontFamily: '"IBM Plex Sans", sans-serif',
        fontWeight: 300,
        fontSize: '1rem',
        color: 'var(--grey-700)',
        lineHeight: 1.9,
        maxWidth: '40rem',
        marginBottom: '5rem'
      }} data-id="element-147">
          {t.teamSubtitle}
        </p>

        {/* Founder Section */}
        <div style={{
        backgroundColor: 'var(--black)',
        display: 'grid',
        gridTemplateColumns: 'repeat(auto-fit, minmax(300px, 1fr))',
        marginBottom: '6rem',
        overflow: 'hidden'
      }} data-id="element-148">
          <FounderVisual data-id="element-149" />
          <div style={{
          padding: '4rem 3rem',
          display: 'flex',
          flexDirection: 'column',
          justifyContent: 'center'
        }} data-id="element-150">
            <h3 style={{
            fontFamily: '"IBM Plex Sans", sans-serif',
            fontWeight: 700,
            fontSize: '1.5rem',
            color: 'var(--white)',
            margin: '0 0 0.25rem 0'
          }} data-id="element-151">
              {renderName(team.founder.name, team.founder.nameZh)}
            </h3>
            <p style={{
            fontFamily: '"IBM Plex Mono", monospace',
            fontSize: '0.65rem',
            letterSpacing: '0.15em',
            textTransform: 'uppercase',
            color: 'var(--red)',
            margin: '0 0 2rem 0'
          }} data-id="element-152">
              {team.founder.title}
            </p>
            <p style={{
            fontFamily: '"IBM Plex Sans", sans-serif',
            fontWeight: 300,
            fontSize: '0.875rem',
            color: 'rgba(255,255,255,0.6)',
            lineHeight: 1.8,
            marginBottom: '1.5rem'
          }} data-id="element-153">
              {team.founder.background}
            </p>
            <p style={{
            fontFamily: '"IBM Plex Sans", sans-serif',
            fontWeight: 300,
            fontSize: '0.875rem',
            color: 'rgba(255,255,255,0.45)',
            lineHeight: 1.8,
            margin: 0
          }} data-id="element-154">
              {team.founder.responsibility}
            </p>
          </div>
        </div>

        {/* Global Team Structure */}
        <div style={{
        marginBottom: '6rem'
      }} data-id="element-155">
          <GlobalNetwork data-id="element-156" />
          <p style={{
          fontFamily: '"IBM Plex Mono", monospace',
          fontSize: '0.75rem',
          letterSpacing: '0.2em',
          textTransform: 'uppercase',
          color: 'var(--grey-500)',
          textAlign: 'center',
          margin: 0
        }} data-id="element-157">
            {team.globalTitle}
          </p>
        </div>

        {/* US Team */}
        <div style={{
        marginBottom: '6rem'
      }} data-id="element-158">
          <h3 style={{
          fontFamily: '"IBM Plex Sans", sans-serif',
          fontWeight: 700,
          fontSize: 'clamp(1.25rem, 2.5vw, 1.75rem)',
          color: 'var(--black)',
          marginBottom: '3rem'
        }} data-id="element-159">
            {team.usTitle}
          </h3>
          <div style={{
          display: 'grid',
          gridTemplateColumns: 'repeat(auto-fit, minmax(250px, 1fr))',
          gap: '0',
          borderTop: '1px solid rgba(0,0,0,0.08)'
        }} data-id="element-160">
            {team.usMembers.map((member, idx) => <div key={idx} style={{
            padding: '2.5rem 2rem 2.5rem 0',
            borderRight: idx < team.usMembers.length - 1 ? '1px solid rgba(0,0,0,0.08)' : 'none',
            paddingLeft: idx > 0 ? '2rem' : 0
          }} data-id="element-161">
                <MemberNodeIcon data-id="element-162" />
                {member.title && <p style={{
              fontFamily: '"IBM Plex Mono", monospace',
              fontSize: '0.65rem',
              letterSpacing: '0.15em',
              textTransform: 'uppercase',
              color: 'var(--red)',
              margin: '0 0 1.5rem 0'
            }} data-id="element-163">
                    {member.title}
                  </p>}
                {member.background && <p style={{
              fontFamily: '"IBM Plex Sans", sans-serif',
              fontWeight: 300,
              fontSize: '0.8125rem',
              color: 'var(--grey-500)',
              lineHeight: 1.6,
              marginBottom: '1rem'
            }} data-id="element-164">
                    {member.background}
                  </p>}
                {member.responsibility && <p style={{
              fontFamily: '"IBM Plex Sans", sans-serif',
              fontWeight: 300,
              fontSize: '0.8125rem',
              color: 'var(--grey-700)',
              lineHeight: 1.6,
              margin: 0
            }} data-id="element-165">
                    {member.responsibility}
                  </p>}
              </div>)}
          </div>
          {/* Recruiting indicator */}
          <div style={{
          borderTop: '1px solid rgba(0,0,0,0.08)',
          padding: '1.5rem 0',
          display: 'flex',
          alignItems: 'center',
          gap: '0.75rem'
        }} data-id="element-166">
            <span style={{
            display: 'inline-block',
            width: '6px',
            height: '6px',
            backgroundColor: 'var(--red)',
            borderRadius: '50%',
            flexShrink: 0
          }} data-id="element-167" />
            <p style={{
            fontFamily: '"IBM Plex Mono", monospace',
            fontSize: '0.65rem',
            letterSpacing: '0.2em',
            textTransform: 'uppercase',
            color: 'var(--grey-500)',
            margin: 0
          }} data-id="element-168">
              {lang === 'zh' ? '更多成员招募中 …' : 'Actively recruiting new members …'}
            </p>
          </div>
        </div>

        {/* China Team */}
        <div style={{
        marginBottom: '8rem'
      }} data-id="element-169">
          <h3 style={{
          fontFamily: '"IBM Plex Sans", sans-serif',
          fontWeight: 700,
          fontSize: 'clamp(1.25rem, 2.5vw, 1.75rem)',
          color: 'var(--black)',
          marginBottom: '3rem'
        }} data-id="element-170">
            {team.cnTitle}
          </h3>
          <div style={{
          display: 'grid',
          gridTemplateColumns: 'repeat(auto-fit, minmax(300px, 1fr))',
          gap: '0',
          borderTop: '1px solid rgba(0,0,0,0.08)'
        }} data-id="element-171">
            {team.cnMembers.map((member, idx) => <div key={idx} style={{
            padding: '2.5rem 2rem',
            borderRight: idx % 2 === 0 ? '1px solid rgba(0,0,0,0.08)' : 'none',
            borderBottom: idx < 2 ? '1px solid rgba(0,0,0,0.08)' : 'none',
            paddingLeft: idx % 2 === 0 ? 0 : '2rem',
            paddingRight: idx % 2 === 0 ? '2rem' : 0
          }} data-id="element-172">
                <MemberNodeIcon data-id="element-173" />
                <p style={{
              fontFamily: '"IBM Plex Sans", sans-serif',
              fontWeight: 600,
              fontSize: '0.9375rem',
              color: 'var(--black)',
              margin: '0 0 0.25rem 0',
              letterSpacing: '-0.01em'
            }} data-id="element-174">
                  {renderName(member.name, member.nameZh)}
                </p>
                <p style={{
              fontFamily: '"IBM Plex Mono", monospace',
              fontSize: '0.65rem',
              letterSpacing: '0.15em',
              textTransform: 'uppercase',
              color: 'var(--red)',
              margin: '0 0 1.5rem 0'
            }} data-id="element-175">
                  {member.title}
                </p>
                <p style={{
              fontFamily: '"IBM Plex Sans", sans-serif',
              fontWeight: 300,
              fontSize: '0.8125rem',
              color: 'var(--grey-500)',
              lineHeight: 1.6,
              marginBottom: '1rem'
            }} data-id="element-176">
                  {member.background}
                </p>
                <p style={{
              fontFamily: '"IBM Plex Sans", sans-serif',
              fontWeight: 300,
              fontSize: '0.8125rem',
              color: 'var(--grey-700)',
              lineHeight: 1.6,
              margin: 0
            }} data-id="element-177">
                  {member.responsibility}
                </p>
              </div>)}
          </div>
        </div>

        {/* Join Section */}
        <div style={{
        textAlign: 'center',
        padding: '4rem 0',
        borderTop: '1px solid rgba(0,0,0,0.08)'
      }} data-id="element-178">
          <h3 style={{
          fontFamily: '"IBM Plex Sans", sans-serif',
          fontWeight: 700,
          fontSize: 'clamp(1.25rem, 2.5vw, 1.75rem)',
          color: 'var(--black)',
          marginBottom: '1.5rem'
        }} data-id="element-179">
            {team.joinTitle}
          </h3>
          <p style={{
          fontFamily: '"IBM Plex Sans", sans-serif',
          fontWeight: 300,
          fontSize: '1rem',
          color: 'var(--grey-500)',
          lineHeight: 1.8,
          maxWidth: '36rem',
          margin: '0 auto 3rem'
        }} data-id="element-180">
            {team.joinText}
          </p>
          <Link to="/contact" style={{
          textDecoration: 'none'
        }} data-id="element-181">
            <button style={{
            fontFamily: '"IBM Plex Sans", sans-serif',
            fontWeight: 300,
            fontSize: '0.875rem',
            letterSpacing: '0.15em',
            textTransform: 'uppercase',
            color: 'var(--red)',
            backgroundColor: 'transparent',
            border: '1px solid var(--red)',
            borderRadius: 0,
            padding: '1rem 2.5rem',
            cursor: 'pointer',
            transition: 'background-color 0.2s ease, color 0.2s ease'
          }} onMouseEnter={e => {
            e.currentTarget.style.backgroundColor = 'var(--red)';
            e.currentTarget.style.color = 'var(--white)';
          }} onMouseLeave={e => {
            e.currentTarget.style.backgroundColor = 'transparent';
            e.currentTarget.style.color = 'var(--red)';
          }} data-id="element-182">
              {lang === 'zh' ? '联系我们' : 'Contact Us'}
            </button>
          </Link>
        </div>
      </motion.div>
    </section>;
}